/**
 * "Can I brew this?" — a pure comparison of a recipe against the cupboard
 * (PLAN §3 V1).
 *
 * ## The three questions, one allocator
 *
 * The editor's per-row chip, the recipes list's badge and the shopping list are
 * the same computation asked at three grains, so they share one allocator:
 *
 * 1. **Requirements** are read off the recipe, one per ingredient row, in
 *    canonical inventory units (grams, or packs for yeast). One per salt of
 *    the water section too: that is the only door a salt has since the picker
 *    stopped offering the nine of them as miscs (PLAN-UX-PASS C4), and a bag
 *    of gypsum is bought and weighed like any other.
 * 2. **Lots are matched** — on the link to one of the brewer's own ingredients
 *    first, then on `catalogRef.id`, then on normalised name — and consumed
 *    **oldest first** ({@link compareInventoryLots}), so the answer is a
 *    deterministic function of the workspace and every device reaches the
 *    same one. The link rung exists because the name rung could not see a
 *    custom malt with a maker: the row says `"Weyermann Pilsner"`, the sack
 *    says `"Pilsner"`, and they never met (PLAN-INGREDIENT-LINKS C1).
 * 3. **Stock is allocated from a shared pool**, in row order. This is the part
 *    that a naive per-row comparison gets wrong: 25 g of Citra against a recipe
 *    with a 20 g bittering charge *and* a 20 g whirlpool addition is not "both
 *    fine". The first row takes 20, the second sees 5, and the recipe is short
 *    15 g. That is the honest answer and it is the same arithmetic the brew-day
 *    deduction performs, which is why the two can never disagree.
 *
 * ## What is deliberately not checked
 *
 * A misc addition measured in millilitres or in units (`each`) — lactic acid,
 * Whirlfloc tablets — has no comparable stock figure: inventory counts miscs in
 * grams (see `document/inventory.ts` on why one unit per kind). Rather than
 * invent a density or a tablet mass, those rows are left out of the check
 * entirely and say so, instead of reporting a confident "missing" for a bottle
 * of acid that is sitting on the shelf.
 */

import { CATALOG_VERSION, getMisc } from '@beerwright/catalog';
import type { CatalogRef } from '../document/common';
import type { CustomIngredientDoc } from '../document/custom-ingredient';
import type { RecipeSaltAddition } from '../document/ingredients';
import type { InventoryItemDoc, InventoryKind, InventoryUnit } from '../document/inventory';
import { compareInventoryLots, inventoryUnitOf, roundQuantity } from '../document/inventory';
import type { RecipeDoc } from '../document/recipe';

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

/** The inventory table, as it sits on {@link WorkspaceState}. */
export type InventoryTable = Readonly<Record<string, InventoryItemDoc>>;

/** The custom-ingredient table, as it sits on {@link WorkspaceState}. */
export type CustomIngredientTable = Readonly<Record<string, CustomIngredientDoc>>;

/**
 * The link an item effectively carries: its id when the library holds it,
 * `null` when it does not.
 *
 * Ids come back from the dead — a restored recipe version, a replayed outbox,
 * a cloned document — so rather than police every entry point, the matcher
 * reads an id that does not resolve as absent and falls through to the next
 * rung (D-IL8). `null` for the table means "do not check": the callers that
 * have no library to hand (a test, a batch tool) take ids at face value, which
 * is never *wrong*, only occasionally stricter than the resolved answer.
 *
 * A row that is missing the field entirely — an older document, a fixture that
 * predates the link — arrives here as `undefined`, and reads as unlinked for
 * the same reason a dead id does: absence of a link is not a link.
 */
export function linkedIngredientId(
  ingredientId: string | null,
  customIngredients: CustomIngredientTable | null,
): string | null {
  if (ingredientId === null || ingredientId === undefined) return null;
  if (customIngredients === null) return ingredientId;
  return Object.hasOwn(customIngredients, ingredientId) ? ingredientId : null;
}

/**
 * How well stocked one ingredient is.
 *
 * Three values, not four: "I have some but not enough" is a different decision
 * from "I have none" — the first is a top-up, the second is a substitution or a
 * shopping trip — and collapsing them would lose the distinction the shopping
 * list is built on.
 */
export type AvailabilityStatus = 'enough' | 'partial' | 'missing';

/**
 * *Which* ingredient something is — the whole of what {@link lotMatches} reads.
 *
 * Named separately from {@link IngredientRequirement} so that a caller which
 * has an identity but no demand — pricing a shortfall, say — can ask the one
 * matcher rather than reimplementing the ladder as a key comparison. Key
 * equality is strictly *narrower* than matching (the rungs fall through, keys
 * do not), and the two drifting apart is how a lot that stocks a row stops
 * being allowed to price it.
 */
export interface IngredientIdentity {
  readonly kind: InventoryKind;
  /** The link to one of the brewer's own ingredients, already resolved, or `null`. */
  readonly ingredientId: string | null;
  /** Catalog provenance, or `null`. */
  readonly catalogRef: CatalogRef | null;
  /** Display name, matched after both ids fall through. */
  readonly name: string;
}

/**
 * One demand a recipe makes on the cupboard.
 *
 * Usually one ingredient row. Not always: the water section's salts are demands
 * too, and one of those is every dose of a salt summed rather than any row
 * (PLAN-UX-PASS C4). See {@link itemId}.
 */
export interface IngredientRequirement extends IngredientIdentity {
  /** What the matcher groups on. See {@link requirementKey}. */
  readonly key: string;
  readonly kind: InventoryKind;
  /**
   * The recipe item's id — what the editor keys its chip on.
   *
   * A salt of the water section has no row to borrow one from, so it carries a
   * derived `salt:<saltId>` (see `saltItemId`). Nothing on screen has that id,
   * which means no per-row chip resolves it: the recipe's badge, the shopping
   * list and the brew-day deduction count the salt, and the Salts section draws
   * no dot beside it.
   */
  readonly itemId: string;
  /** The recipe item's display name. */
  readonly name: string;
  /** The row's link to one of the brewer's own ingredients, already resolved, or `null`. */
  readonly ingredientId: string | null;
  /** The recipe item's catalog provenance, or `null` for a custom ingredient. */
  readonly catalogRef: CatalogRef | null;
  /** The unit {@link required} is expressed in: grams, or packs for yeast. */
  readonly unit: InventoryUnit;
  /** How much this row needs, in {@link unit}. */
  readonly required: number;
}

/** Where one requirement's stock came from. */
export interface LotAllocation {
  readonly inventoryItemId: string;
  /** How much this lot supplies, in the requirement's unit. */
  readonly quantity: number;
}

/** A requirement, answered. */
export interface AvailabilityLine extends IngredientRequirement {
  readonly status: AvailabilityStatus;
  /** What was on the shelf *for this row*, after earlier rows took theirs. */
  readonly held: number;
  /** `required − held`, floored at zero. */
  readonly shortfall: number;
  /** Which lots supply it, oldest first. Empty when nothing matched. */
  readonly allocations: readonly LotAllocation[];
}

/** A whole recipe, answered. */
export interface RecipeAvailability {
  readonly recipeId: string;
  /** The worst line's status — what the list badge and the header chip show. */
  readonly status: AvailabilityStatus;
  readonly lines: readonly AvailabilityLine[];
  /** How many lines are `missing`. */
  readonly missingCount: number;
  /** How many lines are `partial`. */
  readonly partialCount: number;
  /**
   * True when nothing in the workspace could be checked — an empty cupboard, or
   * a recipe with no checkable ingredients.
   *
   * The UI uses it to stay quiet rather than badging every recipe red on a
   * workspace that has never used inventory.
   */
  readonly unchecked: boolean;
}

/** One line of the aggregated shopping list. */
export interface ShoppingListLine {
  readonly key: string;
  readonly kind: InventoryKind;
  readonly name: string;
  /** The line's link to one of the brewer's own ingredients, already resolved, or `null`. */
  readonly ingredientId: string | null;
  readonly catalogRef: CatalogRef | null;
  readonly unit: InventoryUnit;
  /** Total demand across every selected recipe, in {@link unit}. */
  readonly required: number;
  /** What the cupboard holds against it. */
  readonly held: number;
  /** What has to be bought: `required` minus what could actually be allocated. */
  readonly shortfall: number;
  /** Which of the selected recipes want it, in selection order. */
  readonly recipeNames: readonly string[];
}

/** What {@link shoppingList} produces. */
export interface ShoppingList {
  /** The recipes it was computed from, in the order they were given. */
  readonly recipeIds: readonly string[];
  readonly recipeNames: readonly string[];
  /** Only the lines with something to buy, grouped by kind then name. */
  readonly lines: readonly ShoppingListLine[];
  /** True when nothing needs buying. */
  readonly complete: boolean;
}

// ---------------------------------------------------------------------------
// Matching
// ---------------------------------------------------------------------------

/**
 * Comparison tolerance: **one milligram**, or a thousandth of a yeast pack.
 *
 * It has to be coarser than the storage grid, not finer than it. Every figure
 * here — each row's requirement, each lot's stock, each allocation — is rounded
 * onto {@link roundQuantity}'s six-decimal grid *separately*, so a sum of rows
 * can land a grid step or two away from the single number the same total was
 * typed as. One ounce of Citra split into two half-ounce charges is the case
 * that reaches a brewer: 14.174762 g twice is 28.349524 g, the bag on the shelf
 * is 28.349523 g, and the check used to call that a shortfall — of a millionth
 * of a gram, under a chip that read "1 oz in stock of 1 oz needed".
 *
 * A milligram leaves a thousand grid steps of headroom, which no recipe's worth
 * of rows can eat through, and is still far below anything a shortfall could
 * honestly mean: the finest scale in a homebrew kitchen reads 0.01 g, and the
 * finest cell on screen reads 0.1 g. Nothing real is hidden underneath it.
 */
const EPSILON = 1e-3;

/**
 * Fold a name to its matching form: lower case, accents stripped, everything
 * that is not a letter or a digit collapsed to a single space.
 *
 * Mirrors `@beerwright/catalog`'s `normalize` deliberately rather than importing
 * it: this is a *domain* rule about when two ingredients are the same thing,
 * and it must not change because search ranking was retuned.
 */
export function normalizeIngredientName(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * The key two things must share to be the same ingredient.
 *
 * The link wins when there is one: two rows that both point at the brewer's own
 * Pilsner are one line on the shopping list whatever each was named. Catalog id
 * is next, so "Citra" typed by hand and Citra picked from the catalog are the
 * same hop the moment either has provenance; a name fallback catches the pair
 * that never touched either. The kind is always part of the key — a misc
 * called "Cascade" is not the hop. `ingredientId` is expected already resolved
 * ({@link linkedIngredientId}).
 */
export function requirementKey(
  kind: InventoryKind,
  ingredientId: string | null,
  catalogRef: CatalogRef | null,
  name: string,
): string {
  if (ingredientId !== null) return `${kind}:link:${ingredientId}`;
  return catalogRef === null
    ? `${kind}:name:${normalizeIngredientName(name)}`
    : `${kind}:ref:${catalogRef.id}`;
}

/**
 * Whether a stocked lot is the same ingredient as `requirement`: same kind,
 * then the link, then the catalog id, then the normalised name.
 *
 * Each rung decides only when *both* sides carry its value; a side that lacks
 * it drops the pair to the next rung rather than failing it. That is how a
 * catalog-backed lot still meets a hand-typed row on the name, and how a row
 * whose link has died (read as `null` through the library) still meets the lot
 * that outlived it.
 *
 * Exported because this — not {@link requirementKey} — is the definition of
 * "the same ingredient". Keys group things that are *certainly* the same and
 * cannot express fall-through: a linked row and an identically-named unlinked
 * lot match here and key differently, which is exactly the pair that exists
 * for the whole window between a recipe row being linked and its lot being
 * backfilled. Any caller asking "which lots are this ingredient?" must ask
 * here, or it will answer a strictly smaller set than the allocator spends
 * from.
 */
export function lotMatches(
  item: InventoryItemDoc,
  requirement: IngredientIdentity,
  customIngredients: CustomIngredientTable | null,
): boolean {
  if (item.kind !== requirement.kind) return false;
  const lotLink = linkedIngredientId(item.ingredientId, customIngredients);
  if (requirement.ingredientId !== null && lotLink !== null) {
    return lotLink === requirement.ingredientId;
  }
  if (requirement.catalogRef !== null && item.catalogRef !== null) {
    return item.catalogRef.id === requirement.catalogRef.id;
  }
  return normalizeIngredientName(item.name) === normalizeIngredientName(requirement.name);
}

// ---------------------------------------------------------------------------
// Requirements
// ---------------------------------------------------------------------------

/** Kilograms → the grams inventory counts in. */
function gramsOf(kg: number): number {
  return roundQuantity(kg * 1000);
}

/**
 * The id a salt's requirement carries in place of a row id.
 *
 * Every other requirement is one recipe row and borrows that row's `itemId`. A
 * salt is not a row: it is one or more `saltAddition`s, each with an
 * `additionId` of its own, that add up to one bag. So the id is derived from
 * the salt instead — stable across devices, identical on every recipe, and
 * prefixed so it can never collide with a document id.
 */
function saltItemId(salt: RecipeSaltAddition['salt']): string {
  return `salt:${salt}`;
}

/**
 * Every checkable ingredient row of a recipe, as a demand on the cupboard.
 *
 * In document order — fermentables, hops, yeast, miscs, then the water
 * section's salts — which is the order the editor renders and therefore the
 * order stock is allocated in.
 *
 * @param customIngredients - The workspace's custom-ingredient table, so an id
 *   that no longer resolves reads as absent (D-IL8); `null` takes ids at face
 *   value.
 */
export function recipeRequirements(
  recipe: RecipeDoc,
  customIngredients: CustomIngredientTable | null = null,
): readonly IngredientRequirement[] {
  const requirements: IngredientRequirement[] = [];

  const push = (
    kind: InventoryKind,
    item: {
      readonly itemId: string;
      readonly name: string;
      readonly catalogRef: CatalogRef | null;
      readonly ingredientId: string | null;
    },
    required: number,
  ): void => {
    const ingredientId = linkedIngredientId(item.ingredientId, customIngredients);
    requirements.push({
      key: requirementKey(kind, ingredientId, item.catalogRef, item.name),
      kind,
      itemId: item.itemId,
      name: item.name,
      ingredientId,
      catalogRef: item.catalogRef,
      unit: inventoryUnitOf(kind),
      required,
    });
  };

  for (const item of recipe.fermentables) push('fermentable', item, gramsOf(item.amountKg));
  for (const item of recipe.hops) push('hop', item, gramsOf(item.amountKg));
  for (const item of recipe.yeasts) push('yeast', item, roundQuantity(item.packs));
  for (const item of recipe.miscs) {
    // Only the ones inventory can answer for; see this module's header.
    if (item.unit !== 'g') continue;
    push('misc', item, roundQuantity(item.amount));
  }

  // The water section's salts are the same bag of gypsum a misc row would name
  // (PLAN-UX-PASS C4). Since the picker stopped offering salts as miscs, this
  // is the only door a salt has, and a door the cupboard could not see through
  // meant a brewer typed the addition twice to get both the chemistry and the
  // shopping-list line. A `SaltId` *is* a misc catalog id, so the lot joins on
  // `catalogRef.id` exactly as a misc row's does, and everything downstream —
  // the chip, the shopping list, the cost estimate, the brew-day deduction —
  // follows without knowing salts exist.
  //
  // Doses of one salt across the mash and the sparge are one demand: two bags
  // are not bought because the powder went into two vessels. Insertion order
  // keeps the result a deterministic function of the document.
  const grams = new Map<RecipeSaltAddition['salt'], number>();
  for (const addition of recipe.water?.saltAdditions ?? []) {
    grams.set(addition.salt, (grams.get(addition.salt) ?? 0) + addition.grams);
  }
  for (const [salt, total] of grams) {
    push(
      'misc',
      {
        itemId: saltItemId(salt),
        // The catalog's name, so the line reads like the shelf it is bought
        // from; the id itself only if the entry has gone, which would be a
        // catalog the cupboard can no longer describe rather than a reason to
        // drop the demand.
        name: getMisc(salt)?.name ?? salt,
        catalogRef: { kind: 'misc', id: salt, catalogVersion: CATALOG_VERSION },
        ingredientId: null,
      },
      roundQuantity(total),
    );
  }
  return requirements;
}

// ---------------------------------------------------------------------------
// The allocator
// ---------------------------------------------------------------------------

/**
 * Run a list of requirements against a shared pool of stock.
 *
 * The single place stock is apportioned, used by the recipe check, the shopping
 * list and the brew-day deduction. Nothing here mutates the workspace: the pool
 * is a local `Map` of remaining quantities.
 */
function allocate(
  requirements: readonly IngredientRequirement[],
  inventory: InventoryTable,
  customIngredients: CustomIngredientTable | null,
): readonly AvailabilityLine[] {
  const lots = Object.values(inventory).sort(compareInventoryLots);
  const remaining = new Map<string, number>(lots.map((lot) => [lot.id, lot.quantity]));

  return requirements.map((requirement) => {
    const matched = lots.filter((lot) => lotMatches(lot, requirement, customIngredients));
    const held = roundQuantity(matched.reduce((sum, lot) => sum + (remaining.get(lot.id) ?? 0), 0));

    const allocations: LotAllocation[] = [];
    let outstanding = requirement.required;
    for (const lot of matched) {
      if (outstanding <= EPSILON) break;
      const available = remaining.get(lot.id) ?? 0;
      if (available <= EPSILON) continue;
      const take = roundQuantity(Math.min(available, outstanding));
      if (take <= 0) continue;
      remaining.set(lot.id, roundQuantity(available - take));
      outstanding = roundQuantity(outstanding - take);
      allocations.push({ inventoryItemId: lot.id, quantity: take });
    }

    const shortfall = outstanding <= EPSILON ? 0 : outstanding;
    const status: AvailabilityStatus =
      shortfall <= EPSILON ? 'enough' : held > EPSILON ? 'partial' : 'missing';

    return { ...requirement, status, held, shortfall, allocations };
  });
}

/** Whether one status is worse than another. */
const SEVERITY: Readonly<Record<AvailabilityStatus, number>> = {
  enough: 0,
  partial: 1,
  missing: 2,
};

/** The worst status in a set; `enough` when the set is empty. */
export function worstStatus(statuses: Iterable<AvailabilityStatus>): AvailabilityStatus {
  let worst: AvailabilityStatus = 'enough';
  for (const status of statuses) {
    if (SEVERITY[status] > SEVERITY[worst]) worst = status;
  }
  return worst;
}

// ---------------------------------------------------------------------------
// Entry points
// ---------------------------------------------------------------------------

/**
 * Check one recipe against the cupboard.
 *
 * @param recipe - The recipe (or a batch's `recipeSnapshot`) to check.
 * @param inventory - The workspace's `inventory` table.
 * @param customIngredients - The workspace's custom-ingredient table, so an id
 *   that no longer resolves reads as absent (D-IL8); `null` takes ids at face
 *   value.
 */
export function checkRecipeAvailability(
  recipe: RecipeDoc,
  inventory: InventoryTable,
  customIngredients: CustomIngredientTable | null = null,
): RecipeAvailability {
  const lines = allocate(
    recipeRequirements(recipe, customIngredients),
    inventory,
    customIngredients,
  );
  let missingCount = 0;
  let partialCount = 0;
  for (const line of lines) {
    if (line.status === 'missing') missingCount += 1;
    else if (line.status === 'partial') partialCount += 1;
  }
  return {
    recipeId: recipe.id,
    status: worstStatus(lines.map((line) => line.status)),
    lines,
    missingCount,
    partialCount,
    unchecked: lines.length === 0 || Object.keys(inventory).length === 0,
  };
}

/**
 * Aggregate what has to be bought to brew every one of `recipes`.
 *
 * Demand is summed across recipes *and* across rows within a recipe — three
 * beers that each want 30 g of Citra want 90 g — while the cupboard is counted
 * once, through the same shared pool. That is the whole reason this is not
 * three independent checks glued together.
 *
 * @param customIngredients - The workspace's custom-ingredient table, so an id
 *   that no longer resolves reads as absent (D-IL8); `null` takes ids at face
 *   value.
 * @returns Only the lines with a shortfall, ordered by kind (in
 *   {@link INVENTORY_KINDS} order) then by name, which is how a shopping list
 *   is walked round a shop.
 */
export function shoppingList(
  recipes: readonly RecipeDoc[],
  inventory: InventoryTable,
  customIngredients: CustomIngredientTable | null = null,
): ShoppingList {
  const requirements: IngredientRequirement[] = [];
  const recipeNameByIndex: string[] = [];
  for (const recipe of recipes) {
    for (const requirement of recipeRequirements(recipe, customIngredients)) {
      requirements.push(requirement);
      recipeNameByIndex.push(recipe.name);
    }
  }

  const lines = allocate(requirements, inventory, customIngredients);

  /** One group under construction: the running totals, plus who wants it. */
  interface Group {
    readonly key: string;
    readonly kind: InventoryKind;
    readonly name: string;
    readonly ingredientId: string | null;
    readonly catalogRef: CatalogRef | null;
    readonly unit: InventoryUnit;
    required: number;
    readonly held: number;
    shortfall: number;
    readonly recipes: Set<string>;
  }
  const groups = new Map<string, Group>();

  for (const [index, line] of lines.entries()) {
    const existing = groups.get(line.key);
    const recipeName = recipeNameByIndex[index] ?? '';
    if (existing === undefined) {
      groups.set(line.key, {
        key: line.key,
        kind: line.kind,
        name: line.name,
        ingredientId: line.ingredientId,
        catalogRef: line.catalogRef,
        unit: line.unit,
        required: line.required,
        // The first row of a group sees the group's whole stock, because
        // nothing else has taken from it yet.
        held: line.held,
        shortfall: line.shortfall,
        recipes: new Set([recipeName]),
      });
      continue;
    }
    existing.required = roundQuantity(existing.required + line.required);
    existing.shortfall = roundQuantity(existing.shortfall + line.shortfall);
    existing.recipes.add(recipeName);
  }

  const shortfalls: ShoppingListLine[] = [];
  for (const group of groups.values()) {
    if (group.shortfall <= EPSILON) continue;
    shortfalls.push({
      key: group.key,
      kind: group.kind,
      name: group.name,
      ingredientId: group.ingredientId,
      catalogRef: group.catalogRef,
      unit: group.unit,
      required: group.required,
      held: group.held,
      shortfall: group.shortfall,
      recipeNames: [...group.recipes].filter((name) => name !== ''),
    });
  }

  const KIND_ORDER: Readonly<Record<InventoryKind, number>> = {
    fermentable: 0,
    hop: 1,
    yeast: 2,
    misc: 3,
  };
  shortfalls.sort(
    (a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || a.name.localeCompare(b.name),
  );

  return {
    recipeIds: recipes.map((recipe) => recipe.id),
    recipeNames: recipes.map((recipe) => recipe.name),
    lines: shortfalls,
    complete: shortfalls.length === 0,
  };
}

/**
 * Internal: the allocator, exposed for the deduction planner.
 *
 * @param customIngredients - The workspace's custom-ingredient table, so an id
 *   that no longer resolves reads as absent (D-IL8); `null` takes ids at face
 *   value.
 */
export function allocateRequirements(
  requirements: readonly IngredientRequirement[],
  inventory: InventoryTable,
  customIngredients: CustomIngredientTable | null = null,
): readonly AvailabilityLine[] {
  return allocate(requirements, inventory, customIngredients);
}
